import React from "react";

import Spinner from "../../../../components/spinner.components";
import { useParams } from "react-router-dom";
import { useGetPostsByIdAdminHttp } from "../../../../hooks/http/admin/posts/get-post-by-id.admin.http";
import {
  PostStatus,
  type IPost,
} from "../../../../types/models/post.models.types";
import { CampaignPostActionAdminPartial } from "../../home/campaign-posts/campaign-post-action.admin.partials";
import { InstagramEmbed, YouTubeEmbed } from "react-social-media-embed";
import { useToast } from "../../../../hooks/use-toast";
import { handleHttpError } from "../../../../utils/utils";
import RejectionCard from "../../../../components/rejection-card.components";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../components/ui/accordion";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDescription,
  TimelineHeader,
  TimelineIcon,
  TimelineItem,
  TimelineTitle,
} from "../../../../components/ui/timeline";
import { DateTime } from "luxon";
import PostIcon from "../../../../assets/svgs/common/post.svg";
import CrossIcon from "../../../../assets/svgs/common/cross.svg";
import CheckIcon from "../../../../assets/svgs/common/check.svg";
import PostMetric from "../../../../components/post-metrics.components";
import useCampaignStore from "../../../../store/campaign.store";
import { SocialApplicationStatus, SocialPlatforms } from "../../../../types/models/social.models.types";
import { Badge } from "../../../../components/ui/badge";
import { pauseOtherVideos, pauseCurrentVideo } from "../../../../utils/utils";

export function handleIcon(status: PostStatus) {
  switch (status) {
    case PostStatus.post:
      return <PostIcon />;
    case PostStatus.draft:
      return <PostIcon />;
    case PostStatus.draftRejected:
      return <CrossIcon />;
    case PostStatus.postRejected:
      return <CrossIcon />;
    case PostStatus.flaggedReview:
      return <CrossIcon />;
    case PostStatus.flagged:
      return <CrossIcon />;
    case PostStatus.disqualify:
      return <CrossIcon />;
    case PostStatus.postApproved:
      return <CheckIcon />;
    default:
      return <PostIcon />;
  }
}

export const PostSliderMenuPartial: React.FunctionComponent = () => {
  const { postId } = useParams();
  const { execute: getPostByIdHttp, fetching: fetchingPost } =
    useGetPostsByIdAdminHttp();
  const [postData, setPostData] = React.useState<IPost>();
  const { toast } = useToast();
  const { data: campaignData } = useCampaignStore();

  async function getPost() {
    if (postId) {
      const response = await getPostByIdHttp({
        body: null,
        pathParams: { id: postId },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      setPostData(response.data.post);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    pauseCurrentVideo();
    getPost();
  }, [postId]);

  return fetchingPost ? (
    <div className="flex justify-center items-center w-full h-full">
      <Spinner width={30} />
    </div>
  ) : (
    postData && (
      <div className="flex flex-col w-full">
        <div className="flex items-center gap-6 py-4 w-full">
          <img
            src="/static/profile.png"
            className="rounded-xl"
            width={72}
            height={72}
            alt=""
          />
          <span className="font-semibold text-3xl flex flex-col items-start gap-1">
            {postData.creator?.name} 
            {
              postData.social?.applicationStatus?.status === SocialApplicationStatus.rejected &&
              <Badge className="border border-rejectbuttonborder bg-rejectbuttonbg text-rejectbuttontext">
                banned
              </Badge>
            }
          </span>
        </div>
        <div className="pb-5 border-border border-t w-full scale-x-110" />

        {postData.postStatus.status === PostStatus.flaggedReview ||
          (postData.postStatus.status === PostStatus.flagged && (
            <RejectionCard
              status={PostStatus.flaggedReview}
              message={postData.postStatus.message || ""}
            />
          ))}

        {(postData.postStatus.status === PostStatus.post ||
          postData.postStatus.status === PostStatus.postRejected ||
          postData.postStatus.status === PostStatus.postApproved ||
          postData.postStatus.status === PostStatus.flagged ||
          postData.postStatus.status === PostStatus.flaggedReview ||
          postData.postStatus.status === PostStatus.disqualify) && (
          <>
            <div className="bg-tertiarybuttonbg my-5 px-10 py-3 w-fit -translate-x-6">
              Submitted Post
            </div>

            <div className="flex flex-col gap-4">
              <PostMetric post={postData} />
              {campaignData.socialPlatform === SocialPlatforms.instagram && (
                <InstagramEmbed
                  url={postData.liveUrl ?? ""}
                  height={650}
                  width={"100%"}
                  captioned={true}
                />
              )}
              {campaignData.socialPlatform === SocialPlatforms.youtube && (
                <YouTubeEmbed
                  key={postData.liveUrl ?? "youtube-embed"}
                  url={postData.liveUrl ?? ""}
                  height={650}
                  width={"100%"}
                />
              )}
            </div>
            {postData.postStatus.status === PostStatus.flaggedReview ? (
              ""
            ) : (
              <div className="bg-tertiarybuttonbg my-5 px-10 py-3 w-fit -translate-x-6">
                Submitted Draft
              </div>
            )}
          </>
        )}

        {postData.postStatus.status === PostStatus.flaggedReview ? (
          <div className="flex flex-col mb-10 w-full">
            <div className="bg-tertiarybuttonbg my-5 px-10 py-3 w-fit -translate-x-6">
              Post insights
            </div>
            <div className="relative flex justify-center items-center w-full">
              {/* biome-ignore lint/a11y/useMediaCaption: <explanation> */}
              <video
                controls={true}
                src={postData.postInsight?.url}
                className="rounded-xl w-full h-full aspect-[9/16] cursor-pointer object-contain"
                onPlay={(e) => pauseOtherVideos(e.currentTarget)}
              />
            </div>
          </div>
        ) : postData.draftAsset ? (
          <div className="mb-10 border border-border rounded-xl w-full h-full overflow-hidden">
            <div className="relative flex justify-center items-center w-full">
              {/* biome-ignore lint/a11y/useMediaCaption: <explanation> */}
              <video
                controls={true}
                src={postData.draftAsset.url}
                className="w-full h-full aspect-[9/16] cursor-pointer object-contain"
                onPlay={(e) => pauseOtherVideos(e.currentTarget)}
              />
            </div>
            <div className="flex flex-col px-6 pt-5 pb-8 w-full font-semibold text-base">
              <p>{postData.draftDescription}</p>
            </div>
          </div>
        ) : (
          <div className="py-10 border border-border rounded-xl w-full h-full overflow-hidden">
            <p className="text-center text-secondary text-sm">
              Draft not available
            </p>
          </div>
        )}

        {postData.draftAsset && postData.social?.applicationStatus?.status !== SocialApplicationStatus.rejected ? (
          <CampaignPostActionAdminPartial post={postData} />
        ) : (
          ""
        )}
        <div className="mt-10">
          <Accordion
            type="single"
            collapsible
            className="bg-tertiarybuttonbg px-5 rounded-xl p"
          >
            <AccordionItem value="item-1">
              <AccordionTrigger className="font-semibold text-2xl">
                Post timeline
              </AccordionTrigger>
              <AccordionContent className="bg-tertiarybuttonbg mb-5 px-5 pt-5 pb-0 border border-border border-dashed rounded-xl">
                <Timeline>
                  {postData.postStatusLog.map((log, index) => {
                    const rejection =
                      log.status === PostStatus.draftRejected ||
                      log.status === PostStatus.postRejected ||
                      log.status === PostStatus.flagged ||
                      log.status === PostStatus.disqualify;
                    return (
                      // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                      <TimelineItem key={index} className="">
                        <TimelineConnector
                          className={`${
                            postData.postStatusLog.length - 1 === index &&
                            "hidden"
                          }`}
                        />
                        <TimelineHeader>
                          <TimelineIcon
                            className={`text-secondary ${postData.postStatusLog.length - 1 === index ? "text-primary" : "text-secondary"}`}
                          >
                            <div className="z-10">{handleIcon(log.status)}</div>
                          </TimelineIcon>
                          <TimelineTitle
                            className={`font-semibold text-base capitalize ${postData.postStatusLog.length - 1 === index ? "text-primary" : "text-secondary"}`}
                          >
                            {log.status}
                          </TimelineTitle>
                        </TimelineHeader>
                        <TimelineContent>
                          <TimelineDescription
                            className={`-mt-3 ml-3 font-semibold text-secondary text-xs ${postData.postStatusLog.length - 1 === index ? "text-primary" : "text-secondary"}`}
                          >
                            <div className="flex flex-col">
                              <p>{log.user?.email}</p>
                              <p>{log.creator?.phoneNumber}</p>
                              <p>
                                {rejection ? `message - ${log.message}` : ""}
                              </p>
                              <p>
                                {DateTime.fromISO(log.timestamp.toString())
                                  .setZone("local")
                                  .toFormat("LLLL dd 'at' hh.mm a")}
                              </p>
                            </div>
                          </TimelineDescription>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                  {/* <TimelineItem>
										<TimelineConnector />
										<TimelineHeader>
											<TimelineIcon />
											<TimelineTitle className="font-semibold text-base text-secondary">
												Post submitted as draft
											</TimelineTitle>
										</TimelineHeader>
										<TimelineContent>
											<TimelineDescription className="-mt-2 font-semibold text-secondary text-xs">
												March 23 at 08.20 am
											</TimelineDescription>
										</TimelineContent>
									</TimelineItem> */}
                </Timeline>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    )
  );
};
